import { AccordionHeader, AccordionItem, AccordionPanel, Button, Option, Text } from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useStores } from '../../../common/hooks';
import { Contact, ContactNotificationType, contactNotificationTypeToDisplayName } from '../../../models';
import { FlexBox } from '../../shared-components/flex-box';
import { FormInput } from '../../shared-components/form/FormInput';
import { SelectOptions } from '../../shared-components/form/SelectOptions';
import { ValidationMessageList } from '../../shared-components/ValidationMessageList';
import './AdditionalContact.scss';

interface AdditionalContactProps {
  rowKey: string;
  contactIndex: number;
  onRemoveContact: () => void;
  contact: Contact;
  isFirstContact: boolean;
}

const AdditionalContactImpl: React.FC<AdditionalContactProps> = (props) => {
  const {
    rowKey,
    contactIndex,
    contact,
    isFirstContact,
    onRemoveContact,
  } = props;

  const {
    nominationWizardPageUiStore,
  } = useStores();

  const isBeingValidated = nominationWizardPageUiStore.isBeingValidated('additional-contact');

  const validation = nominationWizardPageUiStore.additionalContactValidations[contactIndex];

  return (
    <AccordionItem
      value={rowKey}
      className='additional-contact'
    >
      <AccordionHeader
        expandIconPosition='end'
        className={`additional-contact__header ${isBeingValidated && !validation.isValid ? 'red-bg' : 'blue-bg'}`}
      >
        <Text
          weight='semibold'
        >
          {contact.firstName ? `Contact - ${contact.firstName}` : 'Contact'}
        </Text>
      </AccordionHeader>
      {
        isFirstContact === false && (
          <Button
            icon={<DeleteRegular />}
            title='Delete'
            onClick={onRemoveContact}
            appearance='transparent'
          />
        )
      }
      <AccordionPanel
        className='additional-contact__panel'
      >
        <FlexBox
          wrap
          className='panel__row'
          aligns='start'
          isFullWidth
        >
          <FlexBox
            className='row__sub-row'
            aligns='start'
            isFullWidth
          >
            <SelectOptions
              className='field--prefix'
              validationMessage={
                isBeingValidated
                && validation.fieldErrors.prefix?.length
                  ? (
                    <ValidationMessageList
                      messages={validation.fieldErrors.prefix}
                    />
                  )
                  : undefined
              }
              label='Prefix'
              onOptionSelect={(optionData) => contact.prefix = optionData.optionValue ? optionData.optionValue : ''}
              dropdownProps={{
                inlinePopup: true,
                value: contact.prefix ? contact.prefix.trim() : '',
                selectedOptions: [contact.prefix.trim()],
              }}
            >
              <Option value='' text='' />
              <Option value='Mr.'>Mr.</Option>
              <Option value='Mrs.'>Mrs.</Option>
              <Option value='Ms.'>Ms.</Option>
            </SelectOptions>
            <FormInput
              required
              label='First name'
              validationMessage={
                isBeingValidated
                && validation.fieldErrors.firstName?.length
                  ? (
                    <ValidationMessageList
                      messages={validation.fieldErrors.firstName}
                    />
                  )
                  : undefined
              }
              onChange={(firstName) => contact.firstName = firstName}
              inputProps={{
                type: 'text',
                maxLength: 100,
                value: contact.firstName,
              }}
            />
          </FlexBox>
          <FormInput
            required
            label='Last name'
            validationMessage={
              isBeingValidated
              && validation.fieldErrors.lastName?.length
                ? (
                  <ValidationMessageList
                    messages={validation.fieldErrors.lastName}
                  />
                )
                : undefined
            }
            onChange={(lastName) => contact.lastName = lastName}
            inputProps={{
              type: 'text',
              maxLength: 100,
              value: contact.lastName,
            }}
          />
        </FlexBox>

        <FlexBox
          wrap
          className='panel__row'
          aligns='start'
          isFullWidth
        >
          <FormInput
            required
            label='Contact email'
            validationMessage={
              isBeingValidated
              && validation.fieldErrors.emailAddress?.length
                ? (
                  <ValidationMessageList
                    messages={validation.fieldErrors.emailAddress}
                  />
                )
                : undefined
            }
            onChange={(emailAddress) => contact.emailAddress = emailAddress}
            inputProps={{
              type: 'text',
              maxLength: 100,
              value: contact.emailAddress,
            }}
          />
          <SelectOptions
            required
            validationMessage={
              isBeingValidated
              && validation.fieldErrors.contactNotificationTypeId?.length
                ? (
                  <ValidationMessageList
                    messages={validation.fieldErrors.contactNotificationTypeId}
                  />
                )
                : undefined
            }
            label='Notification options'
            onOptionSelect={(optionData) => {
              if (!optionData.optionValue) return;
              contact.contactNotificationTypeId = +optionData.optionValue
            }}
            dropdownProps={{
              selectedOptions: [`${contact.contactNotificationTypeId || ContactNotificationType.AllNotifications}`],
              value: contactNotificationTypeToDisplayName[(contact.contactNotificationTypeId || ContactNotificationType.AllNotifications) as ContactNotificationType],
            }}
          >
            <Option value={`${ContactNotificationType.AllNotifications}`}>All notifications</Option>
            <Option value={`${ContactNotificationType.OnlyWinnerFinalistNotification}`}>Only winner/finalist notification</Option>
          </SelectOptions>
        </FlexBox>
      </AccordionPanel>
    </AccordionItem>
  );
};

export const AdditionalContact = observer(AdditionalContactImpl);
